import { useEffect, useMemo, useState } from 'react'
import { ROBOT_MAP_IMAGES, getRobotIconName } from 'components/RobotOnMapEditable/robotMapUtils'
import type { LocalFloorMapMarker } from 'components/LocalFloorMapStage/LocalFloorMapStage'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'
import {
  compactMappingLocalSnapshot,
  type CompactMappingLocalState,
  type MappingLocalRobotPose,
} from 'utils/mappingLocalOccupancy'
import { ROBOT_MAP_SPRITE_LENGTH_M } from 'utils/robotIconLayout'
import { VENDOR } from 'utils/vendorDisplayName'

type MappingLocalOdom = MappingLocalRobotPose

function finiteOr<T>(value: unknown, fallback: T): number | T {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export function useAutonomicsMappingLocalWebSocket(params: {
  enabled: boolean
  robotSerialNumber?: string
  vendor?: string
  robotColor?: string
  robotType?: string
  originToFrontM?: number | null
}) {
  const { enabled, robotSerialNumber, vendor, robotColor, robotType, originToFrontM } = params
  const [snapshot, setSnapshot] = useState<CompactMappingLocalState | null>(null)
  const [odometryPose, setOdometryPose] = useState<MappingLocalOdom | null>(null)

  useEffect(() => {
    const serial = String(robotSerialNumber || '').trim()
    if (!enabled || !serial || vendor !== VENDOR.AUTONOMICS) {
      setSnapshot(null)
      setOdometryPose(null)
      return
    }
    const onSnapshot = (data: Parameters<typeof compactMappingLocalSnapshot>[0]) => {
      setSnapshot(compactMappingLocalSnapshot(data))
    }
    const onOdometry = (data: MappingLocalOdom | null) => setOdometryPose(data ?? null)
    WebsocketManager.sendMessage(JSON.stringify({ type: 'mapping_local.subscribe', robot_serial_number: serial }))
    WebsocketManager.addEventListener(SOCKET_MESSAGES.MAPPING_LOCAL_SNAPSHOT, onSnapshot)
    WebsocketManager.addEventListener(SOCKET_MESSAGES.MAPPING_LOCAL_ODOMETRY, onOdometry)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.MAPPING_LOCAL_SNAPSHOT, onSnapshot)
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.MAPPING_LOCAL_ODOMETRY, onOdometry)
      WebsocketManager.sendMessage(JSON.stringify({ type: 'mapping_local.unsubscribe', robot_serial_number: serial }))
      setSnapshot(null)
      setOdometryPose(null)
    }
  }, [enabled, robotSerialNumber, vendor])

  const occupancyImage = snapshot?.occupancyImage ?? null

  const marker = useMemo<LocalFloorMapMarker | null>(() => {
    if (!occupancyImage) return null
    const pose = odometryPose || snapshot?.robotPose
    if (!pose) return null
    const px = finiteOr(pose.x, NaN)
    const py = finiteOr(pose.y, NaN)
    if (!Number.isFinite(px) || !Number.isFinite(py)) return null
    const iconName = getRobotIconName(vendor, robotColor, robotType)
    const iconUrl = ROBOT_MAP_IMAGES.find(x => x.name === iconName)?.image
    if (!iconUrl || occupancyImage.resolution <= 0) return null
    const hasMapxy =
      pose.mapX != null &&
      pose.mapY != null &&
      Number.isFinite(Number(pose.mapX)) &&
      Number.isFinite(Number(pose.mapY))
    const gxRaw = hasMapxy
      ? Number(pose.mapX)
      : (px - occupancyImage.originX) / occupancyImage.resolution
    const gyRaw = hasMapxy
      ? Number(pose.mapY)
      : (py - occupancyImage.originY) / occupancyImage.resolution
    if (!Number.isFinite(gxRaw) || !Number.isFinite(gyRaw)) return null
    const gx = Math.max(0, Math.min(occupancyImage.width - 1, gxRaw))
    const gy = Math.max(0, Math.min(occupancyImage.height - 1, gyRaw))
    const angleDeg = finiteOr(
      pose.angleMapDeg != null && Number.isFinite(Number(pose.angleMapDeg))
        ? pose.angleMapDeg
        : pose.angleDeg,
      0
    )
    return {
      x: gx,
      y: gy,
      angleDeg,
      iconUrl,
      iconSizePx: 40,
      resolutionM: occupancyImage.resolution,
      spriteLengthM: typeof originToFrontM === 'number' ? ROBOT_MAP_SPRITE_LENGTH_M : undefined,
      originToFrontM,
      transitionMs: 250,
    }
  }, [occupancyImage, odometryPose, snapshot?.robotPose, vendor, robotColor, robotType, originToFrontM])

  return { occupancyImage, marker }
}
